// Singleton live-prices stream. The old `useLivePrices` hook opened its
// own WebSocket per call site — with multiple cockpit widgets each calling
// it, we accumulated N price-ticker WS + N setInterval(5000) fallbacks.
// All wasteful: the ticker stream is global by design (covers all coins),
// not per-coin.
//
// This module keeps one ticker WS for the whole tab, one REST fallback
// poller, and a single price map every consumer subscribes to via
// useSyncExternalStore. Consumers re-render only when prices actually
// update — and `getPrice` selectors mean a widget on BTC isn't re-rendered
// by an ETH tick.

import { API_BASE_URL, WS_BASE_URL } from '../../../utils/apiConfig';
import { useSyncExternalStore } from 'react';

const REST_POLL_MS = 30_000;
const RECONNECT_MIN_MS = 1500;
const RECONNECT_MAX_MS = 30_000;

let prices = {};
let snapshotRef = { value: prices };
const listeners = new Set();

let ws = null;
let isConnecting = false;
let reconnectDelay = RECONNECT_MIN_MS;
let reconnectTimer = null;
let restPollTimer = null;
let started = false;

function notify() {
  // Coalesce listener fanout into a single rAF tick — high-volume ticker
  // updates would otherwise re-render every consumer per-message.
  scheduleFlush();
}

let pendingFlush = false;
function scheduleFlush() {
  if (pendingFlush) return;
  pendingFlush = true;
  requestAnimationFrame(() => {
    pendingFlush = false;
    snapshotRef = { value: prices };
    for (const fn of [...listeners]) {
      try { fn(); } catch { /* ignore */ }
    }
  });
}

function mergePrices(patch) {
  // Mutate-and-bump: we wrap in a new wrapper object in scheduleFlush()
  // so useSyncExternalStore's cheap equality picks up the change.
  let changed = false;
  for (const [k, v] of Object.entries(patch)) {
    if (prices[k] !== v) { prices[k] = v; changed = true; }
  }
  if (changed) notify();
}

function getWsUrl() {
  if (typeof window === 'undefined') return null;
  if (WS_BASE_URL) return `${WS_BASE_URL}/prices`;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/prices`;
}

async function fetchFromRest() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  try {
    const res = await fetch(`${API_BASE_URL}/api/prices`);
    if (!res.ok) return;
    const body = await res.json();
    if (!body?.success) return;
    const patch = {};
    if (Array.isArray(body.prices)) {
      for (const item of body.prices) {
        const coin = item.symbol.replace(/USDT$/, '');
        patch[coin] = parseFloat(item.price);
      }
    } else if (body.prices && typeof body.prices === 'object') {
      for (const [sym, data] of Object.entries(body.prices)) {
        patch[sym] = typeof data === 'number' ? data : data.price;
      }
    }
    if (Object.keys(patch).length > 0) mergePrices(patch);
  } catch { /* network errors — next poll will retry */ }
}

function openSocket() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  if (isConnecting) return;
  isConnecting = true;
  clearTimeout(reconnectTimer);
  const url = getWsUrl();
  if (!url) { isConnecting = false; return; }
  try { ws = new WebSocket(url); } catch { isConnecting = false; scheduleReconnect(); return; }

  ws.onopen = () => {
    isConnecting = false;
    reconnectDelay = RECONNECT_MIN_MS;
    // /prices is multiplexed onto the shared TradesWebSocketServer (see
    // server.js upgrade handler) — the server only puts the client into its
    // broadcast set after it receives a `subscribe` frame, otherwise the
    // connection just sits in the limbo "connection — requiresSubscription"
    // state and no price ticks ever arrive. We don't need any per-coin
    // filters here (the ticker fan-out is global), so we send an empty
    // selectedCoins set just to flip the client into subscribed mode.
    try {
      // Note: omit `selectedCoins` entirely — the server rejects an empty
      // array as "selectedCoins cannot be empty (omit field for all coins)".
      // We want the global price fan-out, so no coin filter at all.
      ws.send(JSON.stringify({
        type: 'subscribe',
        filters: {
          whaleTradesMinVolume: 0,
          liquidationsMinVolume: 0,
          tradeTypes: [],
        },
      }));
    } catch {
      // socket closed between open and send; reconnect loop will retry.
    }
  };
  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      // Server may send a wholesale snapshot or incremental updates.
      // Both shapes have the same `{ symbol: price }` layout below.
      const payload = msg.prices || msg.data || msg;
      if (payload && typeof payload === 'object') {
        const patch = {};
        for (const [sym, val] of Object.entries(payload)) {
          if (typeof val === 'number') patch[sym] = val;
          else if (val && typeof val === 'object' && typeof val.price === 'number') patch[sym] = val.price;
        }
        if (Object.keys(patch).length > 0) mergePrices(patch);
      }
    } catch { /* malformed */ }
  };
  ws.onclose = () => { isConnecting = false; ws = null; scheduleReconnect(); };
  ws.onerror = () => { /* onclose handles reconnect */ };
}

function scheduleReconnect() {
  clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(() => {
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
    openSocket();
  }, reconnectDelay);
}

function ensureStarted() {
  if (started) return;
  started = true;
  // Kick off REST fetch immediately (instant data) + start poll loop as
  // a fallback heartbeat. The WS provides bulk of updates; REST every 30s
  // patches up gaps.
  fetchFromRest();
  restPollTimer = setInterval(fetchFromRest, REST_POLL_MS);
  openSocket();
}

function subscribe(cb) {
  ensureStarted();
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
function getSnapshot() { return snapshotRef; }

export function useSharedPrices() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot).value;
}

const normalize = (sym) => sym?.toUpperCase().replace(/USDT$|USD$/i, '');

// Returns just the one price the caller asks for. React still re-renders
// on any price tick (we don't have per-coin listeners here yet), but the
// caller receives a scalar so memoised children downstream stay quiet.
export function useSharedPrice(coin) {
  const all = useSharedPrices();
  return all[normalize(coin)] || null;
}

// Same `formatPrice` as the old hook, exposed as a stable module-level
// function so consumers don't pull it through React state.
export function formatPrice(price) {
  if (!price || price === 0) return '$0.00';
  if (price < 0.0001) return `$${price.toFixed(8)}`;
  if (price < 0.01) return `$${price.toFixed(6)}`;
  if (price < 1) return `$${price.toFixed(4)}`;
  if (price < 100) return `$${price.toFixed(2)}`;
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
